import { FAMILIES, familyOf } from "./tokens";

// Words, numbers and shapes for the SMS Spend dashboard. Pure: each takes the server's report
// (backend/api/sms_spend_report.py) or a piece of it.

export function money(value) {
  const v = Number(value) || 0;
  if (Math.abs(v) >= 10000) return `$${(v / 1000).toFixed(1)}K`;
  return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// "8.2¢" under a dollar, else money().
export function cents(value) {
  const v = Number(value) || 0;
  return Math.abs(v) < 1 ? `${(v * 100).toFixed(1)}¢` : money(v);
}

// The direction filter: [server key, button, tooltip]. Outbound is the default.
export const DIRECTIONS = [
  ["outbound", "Outbound", "Texts the clinics sent"],
  ["inbound", "Inbound", "Texts patients sent the clinics"],
  ["all", "Both", "Texts sent and received"],
];

// How the report's texts went, as a verb: "sent", "received" or "sent or received".
export function directionVerb(report) {
  return { inbound: "received", all: "sent or received" }[report?.direction] || "sent";
}

export function count(value) {
  return (Number(value) || 0).toLocaleString("en-US");
}

// Axis ticks: whole dollars when the tick is whole, compact from 1,000 up.
export function axisMoney(value) {
  const v = Number(value) || 0;
  if (v >= 1000) return `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}K`;
  if (Number.isInteger(v)) return `$${v}`;
  return `$${v.toFixed(v < 1 ? 2 : 1)}`;
}

export function percent(part, whole) {
  return whole ? `${Math.round((part / whole) * 100)}%` : "0%";
}

// "2026-10-03" as a local date (new Date("2026-10-03") would be UTC midnight, a day early here).
export function parseDay(iso) {
  const [y, m, d] = String(iso || "").slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function isoDay(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function shortDay(iso) {
  return parseDay(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function dateTime(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function hourWords(hour) {
  if (hour === 0) return "12 AM";
  if (hour === 12) return "12 PM";
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}

// The day-by-day series as days, weeks (from Monday) or months: each with its cost by family.
export function groupSeries(series, granularity) {
  const groups = new Map();
  (series || []).forEach((day) => {
    const date = parseDay(day.date);
    let key;
    let label;
    if (granularity === "week") {
      const monday = new Date(date);
      monday.setDate(date.getDate() - ((date.getDay() + 6) % 7));
      key = isoDay(monday);
      label = `Wk of ${shortDay(key)}`;
    } else if (granularity === "month") {
      key = day.date.slice(0, 7);
      label = date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    } else {
      key = day.date;
      label = shortDay(day.date);
    }
    if (!groups.has(key)) {
      groups.set(key, {
        key, label, from: day.date, to: day.date, texts: 0, segments: 0, cost: 0,
        ...Object.fromEntries(FAMILIES.map((f) => [f.key, 0])),
      });
    }
    const g = groups.get(key);
    g.to = day.date;
    g.texts += day.texts || 0;
    g.segments += day.segments || 0;
    g.cost += day.cost || 0;
    Object.entries(day.sources || {}).forEach(([source, cost]) => {
      g[familyOf(source)] += cost;
    });
  });
  return [...groups.values()];
}

// The cost of each family over the whole range, biggest first.
export function familyTotals(series) {
  const totals = Object.fromEntries(FAMILIES.map((f) => [f.key, 0]));
  (series || []).forEach((day) => {
    Object.entries(day.sources || {}).forEach(([source, cost]) => {
      totals[familyOf(source)] += cost;
    });
  });
  return FAMILIES.map((f) => ({ ...f, cost: totals[f.key] }));
}

// How a character that makes texts expensive is fixed (the server's `fix`).
export const FIX_WORDS = {
  lookalike: "Swap it for its plain look-alike, such as ' or -.",
  invisible: "Invisible: delete it from the template.",
  script: "Another language's letters: one of them makes the whole text expensive.",
  accent: "Write it without the accent.",
  emoji: "Remove the emoji.",
  symbol: "Swap it for a plain character, such as - or *.",
};

// What to fix first, most costly first: each {key, status, title, detail, tab}. `tab` is where
// the dashboard shows the detail.
export function insights(report) {
  if (!report) return [];
  const totals = report.totals || {};
  const texting = totals.cost || 0;
  const out = [];
  if (report.spike?.alert) {
    out.push({
      key: "spike", status: "critical", tab: "overview", weight: Infinity,
      title: `Today's texting is ${report.spike.ratio ? `${report.spike.ratio}×` : "far above"} the week before`,
      detail: `${money(report.spike.today)} so far today, against ${money(report.spike.average)} a day over the 7 days before.`,
    });
  }
  // A template can be shortened; what patients write can't.
  const longest = (report.sources || []).filter((s) => s.avg_segments > 3 && s.key !== "received")
    .sort((a, b) => b.cost - a.cost)[0];
  if (longest) {
    out.push({
      key: "long-source", status: "serious", tab: "waste", weight: longest.cost,
      title: `${longest.label} are ${longest.avg_segments} segments each`,
      detail: `${count(longest.texts)} texts cost ${money(longest.cost)}, ${percent(longest.cost, texting)} of what texting cost. `
        + "Each segment is billed, so a shorter template costs less on every send.",
    });
  }
  const character = (report.characters || [])[0];
  const plain = totals.plain_characters || {};
  if (character && plain.cost > 0) {
    out.push({
      key: "characters", status: "warning", tab: "waste", weight: plain.cost,
      title: `Plain characters would have saved about ${money(plain.cost)}`,
      detail: `${count(plain.texts)} texts used the expensive format (67 characters a segment, not 153). `
        + `The most common cause: ${character.label}, in ${count(character.texts)} texts`
        + `${character.sources?.length ? ` (${character.sources.join(", ")})` : ""}. ${FIX_WORDS[character.fix] || ""}`,
    });
  }
  if (totals.undelivered_cost > 0) {
    const top = (report.undelivered || [])[0];
    out.push({
      key: "undelivered", status: "critical", tab: "waste", weight: totals.undelivered_cost,
      title: `Paid ${money(totals.undelivered_cost)} for ${count(totals.undelivered_texts)} texts that never arrived`,
      detail: top ? `Most were "${top.label}" (${count(top.texts)} texts, ${money(top.cost)}). Twilio still charges for them.` : "",
    });
  }
  if (totals.split_texts > 0) {
    out.push({
      key: "split", status: "warning", tab: "waste", weight: totals.split_cost,
      title: `${count(totals.split_texts)} texts were re-sent in parts`,
      detail: `Sending a long text again as "Part 1/3" pieces cost ${money(totals.split_cost)}.`,
    });
  }
  const outside = report.tracking?.outside;
  if (outside?.texts > 0) {
    out.push({
      key: "outside", status: "info", tab: "people", weight: outside.cost,
      title: report.include_outside
        ? `${count(outside.texts)} texts (${money(outside.cost)}) have no record in this database`
        : `${count(outside.texts)} texts (${money(outside.cost)}) on the Twilio account are left out`,
      detail: report.include_outside
        ? "No clinic SMS record or send event here names them. They went out on the same Twilio account."
        : 'This database has no record of them. Tick "Include texts outside this database" to count them.',
    });
  }
  const stopped = (report.patients || []).filter((p) => p.verdict === "opt_out" || p.verdict === "bad");
  if (stopped.length) {
    out.push({
      key: "stopped", status: "serious", tab: "patients", weight: stopped.reduce((s, p) => s + p.cost, 0),
      title: `${stopped.length} of the most-texted patients opted out or ${stopped.length === 1 ? "has" : "have"} a bad number`,
      detail: "Check whether texts to them are still worth sending.",
    });
  }
  const rank = { critical: 0, serious: 1, warning: 2, info: 3 };
  return out.sort((a, b) => rank[a.status] - rank[b.status] || b.weight - a.weight);
}
